import { motion } from 'framer-motion';
import { Plus, Layers } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Layout } from '@/components/layout/Layout';
import { QRCodeHistory } from '@/components/qr/QRCodeHistory';
import { UsageIndicator } from '@/components/dashboard/UsageIndicator';
import { useAuthStore } from '@/stores/authStore';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { QRCardSkeleton } from '@/components/ui/skeleton-shimmer';

export default function Dashboard() {
  const { user, isLoading } = useAuthStore();
  const prefersReducedMotion = useReducedMotion();

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <div className="h-8 w-48 bg-muted rounded animate-pulse mb-2" />
            <div className="h-4 w-64 bg-muted rounded animate-pulse" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <QRCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  return (
    <Layout>
      <motion.div 
        className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-10"
        initial={prefersReducedMotion ? {} : "hidden"}
        animate="visible"
        variants={prefersReducedMotion ? {} : containerVariants}
      >
        <motion.div
          variants={prefersReducedMotion ? {} : itemVariants}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8"
        >
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-black tracking-tight mb-1">Dashboard</h1>
            <p className="text-sm text-muted-foreground">
              Manage and track all your QR codes
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Link to="/batch">
              <Button variant="outline" size="sm" className="text-[13px] font-semibold rounded-lg border-border/60 h-9">
                <Layers className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">Batch Create</span>
              </Button>
            </Link>
            <Link to="/create">
              <Button size="sm" className="text-[13px] font-semibold rounded-lg gradient-hero text-white h-9 shadow-sm shadow-primary/20">
                <Plus className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">Create New</span>
              </Button>
            </Link>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <motion.div 
            variants={prefersReducedMotion ? {} : itemVariants}
            className="lg:col-span-3"
          >
            <QRCodeHistory />
          </motion.div>
          
          <motion.div 
            variants={prefersReducedMotion ? {} : itemVariants}
            className="lg:col-span-1"
          >
            <div className="lg:sticky lg:top-24">
              <UsageIndicator />
            </div>
          </motion.div>
        </div>
      </motion.div>
    </Layout>
  );
}
